import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowPathIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XMarkIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from 'recharts';
import supabase from './utils/supabaseClient';
import { formatBRL } from './utils/pricing';

const fmt = (iso) => iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-2">{title}</p>
    <div className="rounded-xl border border-ink-100 overflow-hidden">{children}</div>
  </div>
);

const AdminFinanceiroTab = () => {
  const [refreshKey, setRefreshKey]     = useState(0);
  const [loading, setLoading]           = useState(false);
  const [entregadores, setEntregadores] = useState([]);
  const [pedidos, setPedidos]           = useState([]);
  const [repasses, setRepasses]         = useState([]);
  const [modalEntregadorId, setModalEntregadorId] = useState(null);
  const [selectedPedidoIds, setSelectedPedidoIds] = useState({});
  const [metodo, setMetodo]             = useState('pix');
  const [referencia, setReferencia]     = useState('');
  const [notas, setNotas]               = useState('');
  const [saving, setSaving]             = useState(false);

  // Carrega tudo
  useEffect(() => {
    setLoading(true);
    Promise.all([
      supabase.from('entregadores').select('id, primeiro_nome, sobrenome, whatsapp, status').order('primeiro_nome'),
      supabase.from('pedidos')
        .select('id, codigo_rastreio, entregador_id, preco, valor_entregador, status, repasse_id, entregue_at, endereco_entrega')
        .eq('status', 'entregue')
        .not('entregador_id', 'is', null),
      supabase.from('repasses')
        .select('*')
        .order('pago_em', { ascending: false })
        .limit(50),
    ]).then(([e, p, r]) => {
      setLoading(false);
      if (e.error || p.error || r.error) { toast.error('Erro ao carregar financeiro.'); return; }
      setEntregadores(e.data ?? []);
      setPedidos(p.data ?? []);
      setRepasses(r.data ?? []);
    });
  }, [refreshKey]);

  // Agrupa por entregador
  const byEntregador = useMemo(() => {
    const map = new Map();
    entregadores.forEach((e) => {
      map.set(e.id, {
        entregador: e,
        pendentes: [],
        valorPendente: 0,
        repassados: 0,
        valorRepassado: 0,
      });
    });
    pedidos.forEach((p) => {
      const m = map.get(p.entregador_id);
      if (!m) return;
      const valor = Number(p.valor_entregador || 0);
      if (p.repasse_id) {
        m.repassados += 1;
        m.valorRepassado += valor;
      } else {
        m.pendentes.push(p);
        m.valorPendente += valor;
      }
    });
    return Array.from(map.values())
      .filter((x) => x.pendentes.length > 0 || x.repassados > 0)
      .sort((a, b) => b.valorPendente - a.valorPendente);
  }, [entregadores, pedidos]);

  // KPIs gerais
  const stats = useMemo(() => {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const totalPendente = pedidos
      .filter((p) => !p.repasse_id)
      .reduce((s, p) => s + Number(p.valor_entregador || 0), 0);

    const repassadoMes = repasses
      .filter((r) => new Date(r.pago_em) >= startOfMonth)
      .reduce((s, r) => s + Number(r.valor_total || 0), 0);

    const receitaMes = pedidos
      .filter((p) => p.entregue_at && new Date(p.entregue_at) >= startOfMonth)
      .reduce((s, p) => s + Number(p.preco || 0), 0);

    const lucroFretzMes = pedidos
      .filter((p) => p.entregue_at && new Date(p.entregue_at) >= startOfMonth)
      .reduce((s, p) => s + (Number(p.preco || 0) - Number(p.valor_entregador || 0)), 0);

    return { totalPendente, repassadoMes, receitaMes, lucroFretzMes };
  }, [pedidos, repasses]);

  // Série temporal: receita e lucro Fretz nos últimos 30 dias
  const series30 = useMemo(() => {
    const dias = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      dias.push({
        date: d,
        label: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        receita: 0,
        lucro: 0,
      });
    }
    pedidos.forEach((p) => {
      if (!p.entregue_at) return;
      const d = new Date(p.entregue_at);
      d.setHours(0, 0, 0, 0);
      const idx = dias.findIndex((x) => x.date.getTime() === d.getTime());
      if (idx >= 0) {
        dias[idx].receita += Number(p.preco || 0);
        dias[idx].lucro   += Number(p.preco || 0) - Number(p.valor_entregador || 0);
      }
    });
    return dias.map(({ label, receita, lucro }) => ({
      label,
      receita: Math.round(receita * 100) / 100,
      lucro:   Math.round(lucro   * 100) / 100,
    }));
  }, [pedidos]);

  // Modal de repasse
  const openRepasse = (entregadorId) => {
    setModalEntregadorId(entregadorId);
    const grupo = byEntregador.find((g) => g.entregador.id === entregadorId);
    const sel = {};
    (grupo?.pendentes ?? []).forEach((p) => { sel[p.id] = true; });
    setSelectedPedidoIds(sel);
    setMetodo('pix');
    setReferencia('');
    setNotas('');
  };
  const closeRepasse = () => setModalEntregadorId(null);

  const grupoModal = useMemo(() => {
    if (!modalEntregadorId) return null;
    return byEntregador.find((g) => g.entregador.id === modalEntregadorId);
  }, [modalEntregadorId, byEntregador]);

  const totalSelecionado = useMemo(() => {
    if (!grupoModal) return { count: 0, valor: 0 };
    const items = grupoModal.pendentes.filter((p) => selectedPedidoIds[p.id]);
    return {
      count: items.length,
      valor: items.reduce((s, p) => s + Number(p.valor_entregador || 0), 0),
    };
  }, [grupoModal, selectedPedidoIds]);

  const confirmRepasse = async () => {
    if (!grupoModal) return;
    const ids = grupoModal.pendentes.filter((p) => selectedPedidoIds[p.id]).map((p) => p.id);
    if (ids.length === 0) { toast.error('Selecione ao menos um pedido.'); return; }

    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();

    const { data: repasse, error: errRep } = await supabase
      .from('repasses')
      .insert([{
        entregador_id: grupoModal.entregador.id,
        valor_total: totalSelecionado.valor,
        quantidade_pedidos: totalSelecionado.count,
        metodo,
        referencia: referencia.trim() || null,
        notas: notas.trim() || null,
        created_by: user?.id ?? null,
      }])
      .select()
      .single();

    if (errRep) {
      setSaving(false);
      console.error(errRep);
      toast.error('Erro ao criar repasse.');
      return;
    }

    const { error: errUpd } = await supabase
      .from('pedidos')
      .update({ repasse_id: repasse.id })
      .in('id', ids);

    setSaving(false);

    if (errUpd) {
      console.error(errUpd);
      toast.error('Repasse criado mas falhou ao marcar pedidos. Verifique manualmente.');
      return;
    }

    toast.success(`Repasse de ${formatBRL(totalSelecionado.valor)} registrado.`);
    closeRepasse();
    setRefreshKey((k) => k + 1);
  };

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Receita do mês',   value: formatBRL(stats.receitaMes),    color: 'text-ink-900',    Icon: CurrencyDollarIcon },
          { label: 'Lucro Fretz/mês',  value: formatBRL(stats.lucroFretzMes), color: 'text-brand-700',  Icon: CurrencyDollarIcon },
          { label: 'A repassar',       value: formatBRL(stats.totalPendente), color: 'text-accent-600', Icon: BanknotesIcon },
          { label: 'Repassado/mês',    value: formatBRL(stats.repassadoMes),  color: 'text-green-700',  Icon: CheckCircleIcon },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} className="card p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-ink-500">{label}</p>
              <Icon className={`h-4 w-4 ${color}`} />
            </div>
            <p className={`text-2xl font-display font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Chart receita/lucro últimos 30 dias */}
      <div className="card p-5 mb-8">
        <div className="flex items-center justify-between mb-3">
          <p className="font-display font-bold text-ink-900">Receita e lucro · últimos 30 dias</p>
          <span className="text-xs text-ink-400">
            Receita = total cobrado · Lucro = comissão Fretz
          </span>
        </div>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={series30} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-rec" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"  stopColor="#7D47E4" />
                  <stop offset="100%" stopColor="#42169D" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E9F0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#878AA8' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#878AA8' }} />
              <Tooltip
                formatter={(v) => formatBRL(v)}
                labelStyle={{ color: '#0B0B14', fontWeight: 600 }}
                contentStyle={{ borderRadius: 12, border: '1px solid #E8E9F0' }}
              />
              <Line type="monotone" dataKey="receita" stroke="url(#grad-rec)" strokeWidth={2.5} dot={false} name="Receita" />
              <Line type="monotone" dataKey="lucro" stroke="#22C55E" strokeWidth={2} dot={false} name="Lucro Fretz" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-ink-900">Por entregador</h2>
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          className="btn-ghost text-xs gap-1.5"
          title="Atualizar"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {byEntregador.length === 0 ? (
        <div className="card p-10 text-center text-ink-400">
          <BanknotesIcon className="h-10 w-10 mx-auto mb-3" />
          <p className="text-sm font-medium">Nenhum entregador com movimentação ainda.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {byEntregador.map(({ entregador, pendentes, valorPendente, repassados, valorRepassado }) => (
            <div key={entregador.id} className="card p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-brand-gradient text-white flex items-center justify-center font-display font-bold shadow-glow shrink-0">
                  {(entregador.primeiro_nome || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-ink-900 truncate">
                    {entregador.primeiro_nome} {entregador.sobrenome}
                  </p>
                  <p className="text-xs text-ink-500">{entregador.whatsapp}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:gap-8 text-sm">
                  <div>
                    <p className="text-xs text-ink-400 font-bold uppercase">A repassar</p>
                    <p className="font-display font-bold text-accent-600 text-lg">
                      {formatBRL(valorPendente)}
                    </p>
                    <p className="text-xs text-ink-500">{pendentes.length} pedido{pendentes.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-400 font-bold uppercase">Repassado</p>
                    <p className="font-display font-bold text-green-700 text-lg">
                      {formatBRL(valorRepassado)}
                    </p>
                    <p className="text-xs text-ink-500">{repassados} pedido{repassados !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <button
                  onClick={() => openRepasse(entregador.id)}
                  disabled={pendentes.length === 0}
                  className="btn-primary text-sm gap-1.5 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <BanknotesIcon className="h-4 w-4" />
                  Registrar repasse
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Histórico de repasses */}
      {repasses.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display font-bold text-ink-900 mb-3">Histórico de repasses</h2>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ink-100 bg-ink-50/70">
                    <th className="text-left px-4 py-3 font-semibold text-ink-500">Data</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-500">Entregador</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-500">Método</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-500 hidden md:table-cell">Referência</th>
                    <th className="text-right px-4 py-3 font-semibold text-ink-500">Pedidos</th>
                    <th className="text-right px-4 py-3 font-semibold text-ink-500">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {repasses.map((r) => {
                    const ent = entregadores.find((e) => e.id === r.entregador_id);
                    return (
                      <tr key={r.id} className="border-b border-ink-50 hover:bg-brand-50/30">
                        <td className="px-4 py-3 text-ink-700">{fmt(r.pago_em)}</td>
                        <td className="px-4 py-3 text-ink-900 font-medium">
                          {ent ? `${ent.primeiro_nome} ${ent.sobrenome}` : `#${r.entregador_id}`}
                        </td>
                        <td className="px-4 py-3"><span className="badge">{r.metodo}</span></td>
                        <td className="px-4 py-3 text-ink-500 hidden md:table-cell">{r.referencia || '—'}</td>
                        <td className="px-4 py-3 text-right text-ink-500">{r.quantidade_pedidos}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-700">{formatBRL(r.valor_total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de repasse */}
      {grupoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" onClick={closeRepasse}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="px-6 py-4 border-b border-ink-100 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-ink-400 font-bold uppercase">Repasse para</p>
                <p className="font-display font-bold text-ink-900">
                  {grupoModal.entregador.primeiro_nome} {grupoModal.entregador.sobrenome}
                </p>
              </div>
              <button onClick={closeRepasse} className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400" aria-label="Fechar">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <Section title={`Pedidos pendentes (${grupoModal.pendentes.length})`}>
                <div className="divide-y divide-ink-50 max-h-72 overflow-y-auto">
                  {grupoModal.pendentes.map((p) => (
                    <label key={p.id} className="flex items-center gap-3 px-4 py-3 hover:bg-brand-50/40 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!selectedPedidoIds[p.id]}
                        onChange={(e) => setSelectedPedidoIds((s) => ({ ...s, [p.id]: e.target.checked }))}
                        className="accent-brand-600 h-4 w-4"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-900 truncate">{p.endereco_entrega}</p>
                        <p className="text-xs text-ink-500">
                          {p.codigo_rastreio} · {fmt(p.entregue_at)}
                        </p>
                      </div>
                      <span className="font-bold text-brand-700 shrink-0">{formatBRL(p.valor_entregador)}</span>
                    </label>
                  ))}
                </div>
              </Section>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="r-metodo">Método</label>
                  <select id="r-metodo" className="input" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
                    <option value="pix">Pix</option>
                    <option value="transferencia">Transferência</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="label" htmlFor="r-ref">Referência</label>
                  <input id="r-ref" className="input" type="text" value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    placeholder="Chave Pix, ID do banco…" />
                </div>
              </div>
              <div>
                <label className="label" htmlFor="r-notas">Notas</label>
                <textarea id="r-notas" className="input min-h-[60px] resize-y" value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder="Observações internas (opcional)" />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-ink-100 bg-ink-50/40 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-ink-500">Total selecionado</p>
                <p className="font-display font-bold text-2xl text-brand-700">
                  {formatBRL(totalSelecionado.valor)}
                  <span className="text-sm text-ink-500 font-medium ml-2">
                    ({totalSelecionado.count} pedidos)
                  </span>
                </p>
              </div>
              <button
                onClick={confirmRepasse}
                disabled={saving || totalSelecionado.count === 0}
                className="btn-primary gap-2"
              >
                {saving ? 'Salvando…' : (<><CheckCircleIcon className="h-4 w-4" /> Confirmar repasse</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminFinanceiroTab;
